import React from "react";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { buildWhatsAppLink, EMAIL } from "../constants/site";
import { useLanguage } from "../i18n/LanguageContext";

const ServiceItem = ({ item, idx, s }) => (
  <article id={item.id} data-testid={`service-item-${item.id}`} className="scroll-mt-28 border-t border-black/15 py-10 md:py-12 grid md:grid-cols-12 gap-6 md:gap-10">
    <div className="md:col-span-1 font-sans-aw text-[10px] tracking-luxury uppercase text-black/40">{String(idx + 1).padStart(2, "0")}</div>
    <div className="md:col-span-6">
      <h3 className="font-serif-aw text-2xl md:text-3xl font-normal leading-tight">{item.title}</h3>
      {item.highlight && (
        <p data-testid={`service-highlight-${item.id}`} className="inline-block mt-4 font-sans-aw text-[11px] tracking-luxury uppercase border border-black px-3 py-1.5">
          {item.highlight}
        </p>
      )}
      <p className="font-sans-aw text-sm md:text-base text-black/70 leading-relaxed mt-4">{item.desc}</p>
      <p className="font-sans-aw text-xs text-black/50 mt-4 italic">{s.price_note}</p>
    </div>
    <div className="md:col-span-5 flex flex-col sm:flex-row md:flex-col lg:flex-row gap-3 md:items-start md:justify-end">
      <a
        data-testid={`service-appointment-${item.id}`}
        href={buildWhatsAppLink(s.msg_appointment + item.title)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 bg-black text-white border border-black px-5 py-3 font-sans-aw text-[11px] tracking-luxury uppercase hover:bg-white hover:text-black transition-colors duration-300"
      >
        {s.cta_appointment} <ArrowRight size={13} strokeWidth={1.5} />
      </a>
      <a
        data-testid={`service-inquiry-${item.id}`}
        href={`mailto:${EMAIL}?subject=${encodeURIComponent(s.msg_inquiry + item.title)}`}
        className="inline-flex items-center justify-center gap-2 border border-black/30 px-5 py-3 font-sans-aw text-[11px] tracking-luxury uppercase hover:border-black transition-colors duration-300"
      >
        {s.cta_inquiry}
      </a>
    </div>
  </article>
);

export const ServiceList = () => {
  const { t } = useLanguage();
  const s = t.service_list;
  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section data-testid="service-list" id="leistungsuebersicht" className="px-6 md:px-12 pt-10 pb-16 md:pt-12 md:pb-24">
      <div className="max-w-[1400px] mx-auto">
        <Reveal>
          <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50 mb-6">— {s.eyebrow}</p>
          <h2 className="font-serif-aw text-4xl md:text-5xl font-light tracking-tight leading-[1.05] max-w-3xl">{s.heading}</h2>
          <p className="font-sans-aw text-sm md:text-base text-black/65 mt-6 max-w-2xl leading-relaxed">{s.intro}</p>
        </Reveal>
        <nav aria-label={s.eyebrow} className="flex flex-wrap gap-2 mt-10 mb-12">
          {s.items.map((item) => (
            <button
              key={item.id}
              data-testid={`service-chip-${item.id}`}
              onClick={() => jump(item.id)}
              className="font-sans-aw text-[11px] tracking-luxury-md uppercase border border-black/20 hover:border-black px-3 py-2 transition-colors duration-300"
            >
              {item.title}
            </button>
          ))}
        </nav>
        <div className="border-b border-black/15">
          {s.items.map((item, idx) => (
            <ServiceItem key={item.id} item={item} idx={idx} s={s} />
          ))}
        </div>
      </div>
    </section>
  );
};
